/**
 * Admin utilities for role-based access control
 */

/**
 * Get the current user's email from stored user data
 * @returns {string|null} User email or null if not found
 */
export const getCurrentUserEmail = () => {
  try {
    // Check both localStorage and sessionStorage for user data
    const userData = 
      localStorage.getItem("user_data") || 
      sessionStorage.getItem("user_data");
    
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.email || null;
    }
  } catch (error) {
    console.warn('Failed to parse user data:', error);
  }
  
  return null;
};

/**
 * Get list of admin users from environment variables
 * @returns {string[]} Array of admin email addresses
 */
export const getAdminUsers = () => {
  const adminUsersEnv = process.env.REACT_APP_ADMIN_USERS;
  
  if (!adminUsersEnv || adminUsersEnv.trim() === '') {
    return [];
  }
  
  return adminUsersEnv
    .split(',')
    .map(email => email.trim().toLowerCase())
    .filter(email => email.length > 0);
};

/**
 * Check if the current user is an admin
 * @returns {boolean} True if current user is admin, false otherwise
 */
export const isCurrentUserAdmin = () => {
  const userEmail = getCurrentUserEmail();
  const adminUsers = getAdminUsers();
  
  if (!userEmail || adminUsers.length === 0) {
    return false;
  }
  
  return adminUsers.includes(userEmail.toLowerCase());
};

/**
 * Check if a specific email is an admin
 * @param {string} email - Email to check
 * @returns {boolean} True if email is admin, false otherwise
 */
export const isEmailAdmin = (email) => {
  if (!email) return false;
  
  const adminUsers = getAdminUsers();
  return adminUsers.includes(email.toLowerCase());
};

/**
 * Get admin status info for debugging
 * @returns {object} Admin status information
 */
export const getAdminDebugInfo = () => {
  const userEmail = getCurrentUserEmail();
  const adminUsers = getAdminUsers();
  const isAdmin = isCurrentUserAdmin();
  
  return {
    userEmail,
    adminUsers,
    isAdmin,
    hasAdminUsers: adminUsers.length > 0
  };
};